// ResetPasswordAction.js
import React, { useState, useEffect } from 'react';
import { auth } from './firebase';
import { verifyPasswordResetCode, confirmPasswordReset } from 'firebase/auth';
import { RADIUS, SHADOW, COLORS, labelStyle as themeLabelStyle, inputStyle as themeInputStyle, cardStyle as themeCardStyle } from './theme';
import Button from './components/Button';
import { getPasswordError, PASSWORD_REQUIREMENTS_TEXT } from './passwordValidation';
import { Check } from 'lucide-react';

const cardStyle = themeCardStyle({ padding: '40px', boxShadow: SHADOW.raised, width: '100%', maxWidth: '400px', marginBottom: 0 });
const inputStyle = themeInputStyle({ fontSize: '16px' });
const labelStyle = themeLabelStyle();
// Renders in place of the normal login/dashboard flow whenever the app is opened via a
// password reset link (?mode=resetPassword&oobCode=...) — both the admin-triggered reset-link
// email (UserManagement.js) and the self-service "Forgot password?" link on the login screen
// (RoleLogin.js) now generate links that point *here* (via actionCodeSettings.url +
// handleCodeInApp), landing on cpp-field-service-app.web.app — a domain people already
// recognize as this app — instead of Firebase's generic, unbranded firebaseapp.com action
// handler page.
function ResetPasswordAction({ oobCode, onDone }) {
  const [status, setStatus] = useState('verifying'); // verifying | ready | invalid | success
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    verifyPasswordResetCode(auth, oobCode)
      .then((verifiedEmail) => {
        setEmail(verifiedEmail);
        setStatus('ready');
      })
      .catch(() => setStatus('invalid'));
  }, [oobCode]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const passwordError = getPasswordError(newPassword);
    if (passwordError) {
      setError(passwordError);
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await confirmPasswordReset(auth, oobCode, newPassword);
      setStatus('success');
    } catch (err) {
      setError(err.code === 'auth/expired-action-code' || err.code === 'auth/invalid-action-code'
        ? 'This link has expired or was already used.'
        : 'Failed to reset password: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: COLORS.rowHover }}>
      <div style={cardStyle}>
        <div style={{ marginBottom: '32px', textAlign: 'center' }}>
          <div style={{ backgroundColor: COLORS.sidebarBg, borderRadius: RADIUS.md, padding: '18px 20px' }}>
            <img src="/taskwrx-logo.png" alt="TaskWRX" style={{ width: '100%', maxWidth: '280px', height: 'auto', display: 'block', margin: '0 auto' }} />
          </div>
        </div>

        {status === 'verifying' && (
          <p style={{ textAlign: 'center', color: COLORS.textFaint, fontSize: '13px' }}>Checking your link…</p>
        )}

        {status === 'invalid' && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: COLORS.text, fontWeight: '600' }}>This link has expired or was already used.</p>
            <p style={{ margin: '0 0 24px 0', fontSize: '13px', color: COLORS.textMuted }}>Ask your admin to send a new reset link, or use "Forgot password?" on the login screen.</p>
            <Button onClick={onDone} style={{ width: '100%', fontSize: '14px' }}>Back to Login</Button>
          </div>
        )}

        {status === 'ready' && (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <p style={{ margin: '0 0 4px 0', fontSize: '13px', color: COLORS.textMuted }}>
              Set a new password for <strong>{email}</strong>
            </p>
            <div>
              <label style={labelStyle}>New password</label>
              <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required autoComplete="new-password" style={inputStyle} />
              <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: COLORS.textFaint }}>{PASSWORD_REQUIREMENTS_TEXT}</p>
            </div>
            <div>
              <label style={labelStyle}>Confirm new password</label>
              <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required autoComplete="new-password" style={inputStyle} />
            </div>
            {error && <p style={{ margin: 0, fontSize: '12px', color: COLORS.danger }}>{error}</p>}
            <Button type="submit" disabled={saving} style={{ width: '100%', fontSize: '14px' }}>
              {saving ? 'Saving…' : 'Set New Password'}
            </Button>
          </form>
        )}

        {status === 'success' && (
          <div style={{ textAlign: 'center' }}>
            <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: COLORS.success, fontWeight: '700' }}><Check size={14} strokeWidth={2.5} style={{ verticalAlign: '-2px' }} /> Password updated</p>
            <p style={{ margin: '0 0 24px 0', fontSize: '13px', color: COLORS.textMuted }}>You can now log in with your new password.</p>
            <Button onClick={onDone} style={{ width: '100%', fontSize: '14px' }}>Back to Login</Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ResetPasswordAction;
